import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Zap, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../../context/CartContext";
import { createCheckout } from "../../lib/api";
import { PayPalCheckout } from "./PayPalCheckout";

export const CartDrawer = () => {
  const { items, open, setOpen, removeItem, updateQty, total, count } = useCart();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!email.includes("@")) {
      toast.error("Enter a valid email for your receipt");
      return;
    }
    setLoading(true);
    try {
      const res = await createCheckout({
        email,
        origin_url: window.location.origin,
        items: items.map((i) => ({
          product_id: i.product_id,
          size: i.size,
          color: i.color,
          color_key: i.color_key,
          image_path: i.image,
          qty: i.qty,
          design_id: i.design_id,
        })),
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  const close = () => setOpen(false);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[110] bg-black/50 backdrop-blur-sm"
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[120] w-full max-w-md bg-[#FAF9F5] border-l-2 border-[#111111] text-[#111111] flex flex-col pb-[env(safe-area-inset-bottom)]"
            style={{ paddingBottom: "max(env(safe-area-inset-bottom), 20px)" }}
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between p-5 border-b-2 border-[#111111]">
              <h3 className="font-display font-black uppercase tracking-tighter text-xl">
                Your Cart ({count})
              </h3>
              <button onClick={close} data-testid="cart-close" aria-label="Close cart">
                <X className="h-5 w-5 text-[#555555] hover:text-[#111111]" />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                <Zap className="h-10 w-10 text-[#111111]/20 mb-4" />
                <p className="text-[#555555]">Your cart is empty.</p>
                <p className="text-[#555555]/70 text-sm mt-1">Add gear to get rolling.</p>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-5 pb-4 space-y-4">
                  {items.map((i) => (
                    <div
                      key={i.key}
                      className="flex gap-3 border-b border-[#111111]/10 pb-4"
                      data-testid={`cart-item-${i.product_id}`}
                    >
                      <img
                        src={i.image}
                        alt={i.name}
                        className="h-20 w-16 object-cover rounded-sm bg-[#141414]"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between gap-2">
                          <h4 className="font-bold text-sm uppercase tracking-tight truncate">
                            {i.name}
                          </h4>
                          <button onClick={() => removeItem(i.key)} data-testid={`remove-${i.product_id}`}>
                            <Trash2 className="h-4 w-4 text-[#555555] hover:text-red-600" />
                          </button>
                        </div>
                        <p className="text-xs text-[#555555] mt-0.5">
                          {i.size} · {i.color}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-3 border border-[#111111]/20 rounded-sm px-2 py-1">
                            <button onClick={() => updateQty(i.key, -1)} aria-label="decrease">
                              <Minus className="h-3.5 w-3.5 text-[#333333]" />
                            </button>
                            <span className="text-sm font-bold w-4 text-center">{i.qty}</span>
                            <button onClick={() => updateQty(i.key, 1)} aria-label="increase">
                              <Plus className="h-3.5 w-3.5 text-[#333333]" />
                            </button>
                          </div>
                          <span className="font-display font-bold">
                            ${(i.price * i.qty).toFixed(0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Sticky checkout footer — tight spacing so both CTAs stay above
                    any OS dock/taskbar even on short laptop viewports. */}
                <div
                  className="shrink-0 border-t-2 border-[#111111] bg-[#FAF9F5] px-5 pt-2 pb-3 space-y-1.5"
                  data-testid="checkout-footer"
                >
                  <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email for your receipt"
                    type="email"
                    data-testid="checkout-email"
                    className="w-full bg-white border border-[#111111]/20 rounded-sm px-3 py-1.5 text-sm outline-none focus:border-[#FF4500] transition-colors"
                  />
                  <p className="text-[10px] text-[#555555] leading-tight">
                    Shipping address is collected on the checkout page after you choose Card or PayPal.
                  </p>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-[#555555]">Total</span>
                    <span className="font-display font-black text-lg">${total.toFixed(2)}</span>
                  </div>
                  <button
                    onClick={submit}
                    disabled={loading}
                    data-testid="checkout-submit"
                    className="w-full bg-[#FF4500] text-white py-2.5 rounded-sm font-black uppercase text-xs tracking-wider hover:bg-[#111111] transition-colors duration-200 disabled:opacity-60"
                  >
                    {loading ? "Redirecting…" : "Pay with Card"}
                  </button>

                  <PayPalCheckout
                    email={email}
                    items={items}
                    disabled={loading || items.length === 0}
                  />
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
